import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import SignalRService from '../../../utilities/WS/SignalRService';
import {
    GetPublicConfigBL,
    RegisterParticipantBL,
    SubmitScoreBL,
    GetRankingBL,
    GetParticipantStatusBL,
    TrackMetricBL,
    determineScreamCategory,
    createAudioEngine,
    requestMicrophoneStream,
    requestWakeLock,
    triggerVibration
} from '../../../utilities/BL/PersonalNoisemeter';

// Claves de persistencia local
const STORAGE_KEY = 'interaktia_personal_noisemeter_fan_data';

// Fases de la experiencia móvil
const PHASES = {
    LOADING: 'LOADING',
    CLOSED: 'CLOSED',
    REGISTER: 'REGISTER',
    ALREADY_PARTICIPATED: 'ALREADY_PARTICIPATED',
    MIC_PERMISSION: 'MIC_PERMISSION',
    COUNTDOWN: 'COUNTDOWN',
    RECORDING: 'RECORDING',
    SUBMITTING: 'SUBMITTING',
    RESULT: 'RESULT'
};

const DEFAULT_TERMS_TEXT = `TÉRMINOS Y CONDICIONES DE LA DINÁMICA
1. Organizador de la dinámica: Club Tijuana Xoloitzcuintles de Caliente es la única responsable organizadora de esta dinámica.
2. Deslinde de responsabilidad técnica: Interaktia actúa únicamente como el proveedor tecnológico de la plataforma del juego y queda eximido de cualquier responsabilidad derivada de la entrega de premios o controversias de la dinámica.
3. Participantes: Dinámica válida únicamente para personas presentes en el local que sean mayores de edad o cuenten con la debida autorización.
4. Registro: Cada participante debe registrarse con sus datos reales (Nombre completo, correo electrónico y teléfono celular).
5. Elección del ganador: Los resultados se determinan por la potencia acústica alcanzada en decibeles (dB). En caso de empate, se desempata por el decibel pico más alto y el registro cronológico más temprano registrado por el servidor.
6. Decisiones: Las decisiones del comité organizador son inapelables y definitivas.`;

const DEFAULT_PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico y Teléfono) serán recabados con la única finalidad de registrar su participación en la dinámica "Grito Mexicano", poder contactarle y validar su identidad en caso de resultar ganador(a), así como para estadísticas internas de participación de los organizadores.

Al aceptar este aviso, autoriza expresamente que sus datos de contacto sean compartidos y transferidos entre Club Tijuana Xoloitzcuintles de Caliente y la empresa operadora Interaktia, así como sus asociados comerciales y aliados estratégicos con fines de logística, estadísticas y entrega de premios. No se realizarán transferencias a otros terceros sin su consentimiento. Para dudas o aclaraciones contacte a soporte.`;

/**
 * COMPONENTE PWA MÓVIL DEL AFICIONADO: GRITO MEXICANO (PersonalNoisemeter)
 * Experiencia inmersiva a 60 FPS con Web Audio API, Screen WakeLock, resiliencia 4G y ranking en vivo.
 */
export const PersonalNoisemeter = ({ roomId: propRoomId, config: propConfig }) => {
    // 1. Detección de Sala
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const activeRoomId = propRoomId || searchParams.get('roomId') || localStorage.getItem('active_room_id') || '';

    // 2. Estados Principales
    const [phase, setPhase] = useState(PHASES.LOADING);
    const [config, setConfig] = useState(() => ({
        headerTitle: '¡Grito Mexicano!',
        headerSubtitle: 'Demuestra la fuerza de tu orgullo patrio',
        durationSeconds: 5,
        ticketPrefix: 'GM',
        primaryColor: '#006847',
        backgroundColor: '#121212',
        enableAmbientNoiseCalibration: false,
        enableScoreFormula: false,
        isActive: true,
        termsAndConditionsText: DEFAULT_TERMS_TEXT,
        privacyPolicyText: DEFAULT_PRIVACY_TEXT,
        ...propConfig
    }));

    // Formulario de Registro
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        acceptedTerms: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [isRegistering, setIsRegistering] = useState(false);

    // Datos del Participante Activo
    const [participantData, setParticipantData] = useState(null);

    // Conteo regresivo previo
    const [countdown, setCountdown] = useState(3);

    // Telemetría Acústica en Vivo (RECORDING)
    const [currentDecibels, setCurrentDecibels] = useState(0);
    const [peakDecibels, setPeakDecibels] = useState(0);
    const [remainingSeconds, setRemainingSeconds] = useState(5);

    // Estado de Envío y Reintentos
    const [submitRetryCount, setSubmitRetryCount] = useState(0);
    const [submitError, setSubmitError] = useState(null);

    // Resultado Oficial
    const [officialResult, setOfficialResult] = useState(null);

    // Modal de Ranking en Vivo
    const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
    const [rankingList, setRankingList] = useState([]);
    const [rankingStats, setRankingStats] = useState(null);
    const [isLoadingRanking, setIsLoadingRanking] = useState(false);
    const [rankingSearch, setRankingSearch] = useState('');

    // Modales Legales
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // Alertas y Mensajes Toast
    const [toastMessage, setToastMessage] = useState(null);

    // Telemetría de Aficionado (HU-09)
    const [formStartTracked, setFormStartTracked] = useState(false);

    // Referencias Mutables
    const wakeLockRef = useRef(null);
    const audioEngineRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const animationFrameRef = useRef(null);
    const recordingSamplesRef = useRef([]);
    const recordingStartTimeRef = useRef(null);
    const ambientNoiseBaselineRef = useRef(0);
    const ambientSamplesRef = useRef([]);
    const isRecordingActiveRef = useRef(false);
    const livePeakRef = useRef(0);

    // =========================================================================
    // 2.1 TELEMETRÍA Y EMBUDO DE CONVERSIÓN (HU-09)
    // =========================================================================
    const getSessionId = useCallback(() => {
        let sessId = sessionStorage.getItem('pm_session_id');
        if (!sessId) {
            sessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('pm_session_id', sessId);
        }
        return sessId;
    }, []);

    const trackEvent = useCallback(async (eventName) => {
        const sessId = getSessionId();
        const urlParams = new URLSearchParams(window.location.search);
        const rawSrc = urlParams.get('s') || urlParams.get('src') || urlParams.get('utm_source') || 'direct';

        try {
            await TrackMetricBL(activeRoomId, eventName, sessId, rawSrc);
        } catch (e) {
            console.warn('Error en telemetría local:', e);
        }

        try {
            if (window.gtag) {
                window.gtag('event', `pm_${eventName}`, {
                    session_id: sessId,
                    event_category: 'grito_mexicano',
                    event_label: eventName,
                    traffic_source: rawSrc
                });
            }
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: `pm_${eventName}`,
                    session_id: sessId,
                    traffic_source: rawSrc
                });
            }
        } catch (e) {
            console.warn('Error en telemetría externa (GA4/GTM):', e);
        }
    }, [getSessionId, activeRoomId]);

    const handleFormStart = useCallback(() => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            trackEvent('form_start');
        }
    }, [formStartTracked, trackEvent]);

    // Disparadores automáticos de montaje: page_view y qr_scan
    useEffect(() => {
        if (!sessionStorage.getItem('pm_page_view_tracked')) {
            sessionStorage.setItem('pm_page_view_tracked', 'true');
            trackEvent('page_view');
        }

        const urlParams = new URLSearchParams(window.location.search);
        if ((urlParams.has('s') || urlParams.has('src')) && !sessionStorage.getItem('pm_qr_scan_tracked')) {
            sessionStorage.setItem('pm_qr_scan_tracked', 'true');
            trackEvent('qr_scan');
        }
    }, [trackEvent]);

    // =========================================================================
    // 3. TOAST NOTIFIER HELPER
    // =========================================================================
    const showToast = useCallback((msg, type = 'info', durationMs = 4000) => {
        setToastMessage({ msg, type });
        setTimeout(() => {
            setToastMessage(null);
        }, durationMs);
    }, []);

    // =========================================================================
    // 4. GESTIÓN DE SCREEN WAKELOCK
    // =========================================================================
    const acquireWakeLock = useCallback(async () => {
        try {
            if (!wakeLockRef.current) {
                const lock = await requestWakeLock();
                if (lock) {
                    wakeLockRef.current = lock;
                    lock.addEventListener('release', () => {
                        wakeLockRef.current = null;
                    });
                }
            }
        } catch (e) {
            console.warn('No se pudo activar Screen WakeLock:', e);
        }
    }, []);

    const releaseWakeLockSafely = useCallback(async () => {
        try {
            if (wakeLockRef.current) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        } catch (e) {
            // Ignorar
        }
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && (phase === PHASES.COUNTDOWN || phase === PHASES.RECORDING || phase === PHASES.SUBMITTING)) {
                acquireWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseWakeLockSafely();
        };
    }, [phase, acquireWakeLock, releaseWakeLockSafely]);

    // =========================================================================
    // 5. CARGA INICIAL Y PERSISTENCIA (LOCALSTORAGE + API CONFIG)
    // =========================================================================
    const loadPublicConfig = useCallback(async () => {
        try {
            const res = await GetPublicConfigBL(activeRoomId);
            if (res.success && res.data) {
                setConfig(prev => ({ ...prev, ...res.data }));
                if (res.data.isActive === false) {
                    setPhase(PHASES.CLOSED);
                    return;
                }
            }
        } catch (e) {
            console.warn('Error al cargar config pública:', e);
        }
    }, [activeRoomId]);

    // Inicialización del estado
    useEffect(() => {
        let isMounted = true;

        const initializeSession = async () => {
            await loadPublicConfig();

            // Verificar si existe sesión o resultado previo en LocalStorage
            try {
                const storedRaw = localStorage.getItem(STORAGE_KEY);
                if (storedRaw) {
                    const stored = JSON.parse(storedRaw);
                    if (stored && stored.participantId) {
                        setParticipantData(stored);
                        setFormData(prev => ({
                            ...prev,
                            fullName: stored.fullName || '',
                            email: stored.email || '',
                            phone: stored.phone || ''
                        }));

                        // Consultar estado en el servidor (para detectar si el operador lo reactivó)
                        try {
                            const statusRes = await GetParticipantStatusBL(activeRoomId, stored.participantId);
                            if (statusRes.success && statusRes.data) {
                                const s = statusRes.data;
                                // Si el participante fue archivado o reactivado por el operador
                                if (s.isArchived || s.status === 'RESET_BY_ADMIN') {
                                    localStorage.removeItem(STORAGE_KEY);
                                    setOfficialResult(null);
                                    setParticipantData(null);
                                    setFormData(prev => ({
                                        ...prev,
                                        fullName: s.fullName || stored.fullName || '',
                                        email: s.email || stored.email || '',
                                        phone: s.phone || stored.phone || ''
                                    }));
                                    showToast('¡Tu turno ha sido reactivado por el operador! Puedes volver a intentar.', 'success', 6000);
                                    if (isMounted) setPhase(PHASES.REGISTER);
                                    return;
                                }

                                if (s.status === 'COMPLETED' && stored.result) {
                                    setOfficialResult(stored.result);
                                    if (isMounted) setPhase(PHASES.RESULT);
                                    return;
                                }

                                if (s.status === 'ALREADY_PARTICIPATED') {
                                    if (isMounted) setPhase(PHASES.ALREADY_PARTICIPATED);
                                    return;
                                }
                            }
                        } catch (statusErr) {
                            console.warn('No se pudo verificar el estado en línea del participante:', statusErr);
                        }

                        if (stored.result && stored.status === 'COMPLETED') {
                            setOfficialResult(stored.result);
                            if (isMounted) setPhase(PHASES.RESULT);
                            return;
                        }

                        if (stored.status === 'ALREADY_PARTICIPATED') {
                            if (isMounted) setPhase(PHASES.ALREADY_PARTICIPATED);
                            return;
                        }

                        // Si tiene sesión registrada pendiente de jugar
                        if (stored.sessionToken) {
                            if (isMounted) setPhase(PHASES.MIC_PERMISSION);
                            return;
                        }
                    }
                }
            } catch (e) {
                console.warn('Error al leer LocalStorage:', e);
            }

            if (isMounted) setPhase(PHASES.REGISTER);
        };

        initializeSession();

        return () => {
            isMounted = false;
        };
    }, [loadPublicConfig, activeRoomId, showToast]);

    // =========================================================================
    // 6. SIGNALR: EVENTOS DE RESET Y ACTUALIZACIÓN DE RANKING
    // =========================================================================
    useEffect(() => {
        const handleSignalRMessage = (message) => {
            if (!message || !message.type) return;

            const msgRoomId = message.roomId || message.RoomId;
            if (msgRoomId && activeRoomId && String(msgRoomId).toLowerCase() !== String(activeRoomId).toLowerCase()) {
                return;
            }

            const type = message.type;

            // Operador reactivó al usuario
            if (type === 'personalNoisemeter:resetSession') {
                const resetData = message.data || {};
                const storedRaw = localStorage.getItem(STORAGE_KEY);
                const stored = storedRaw ? JSON.parse(storedRaw) : {};
                const currentId = participantData?.participantId || stored?.participantId;
                const currentEmail = (participantData?.email || stored?.email || formData.email || '').toLowerCase().trim();
                const currentPhone = (participantData?.phone || stored?.phone || formData.phone || '').trim();

                const matchId = resetData.participantId && currentId && String(resetData.participantId).toLowerCase() === String(currentId).toLowerCase();
                const matchEmail = resetData.email && currentEmail && String(resetData.email).toLowerCase() === currentEmail;
                const matchPhone = resetData.phone && currentPhone && String(resetData.phone) === currentPhone;

                if (matchId || matchEmail || matchPhone) {
                    showToast('¡Tu turno ha sido reactivado por el operador! Puedes gritar de nuevo.', 'success', 6000);
                    triggerVibration([200, 100, 200]);

                    // Limpiar datos previos de sesión
                    localStorage.removeItem(STORAGE_KEY);
                    setOfficialResult(null);
                    setParticipantData(null);
                    setPhase(PHASES.REGISTER);
                }
            }

            // Actualización de ranking en tiempo real
            if (type === 'personalNoisemeter:rankingUpdate') {
                if (message.data?.topRanking) {
                    setRankingList(message.data.topRanking);
                    setRankingStats({
                        totalParticipants: message.data.totalParticipants,
                        recordPeakDecibels: message.data.recordPeakDecibels,
                        averageScore: message.data.averageScore
                    });
                }
            }
        };

        SignalRService.on('message', handleSignalRMessage);
        return () => {
            SignalRService.off('message', handleSignalRMessage);
        };
    }, [activeRoomId, participantData, showToast]);

    // =========================================================================
    // 7. REGISTRO DEL PARTICIPANTE
    // =========================================================================
    const handleInputChange = (e) => {
        handleFormStart();
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.fullName || formData.fullName.trim().length < 2) {
            errors.fullName = 'Ingresa tu nombre completo.';
        }
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            errors.email = 'Ingresa un correo electrónico válido.';
        }
        if (!formData.phone || !/^\d{10}$/.test(formData.phone.trim())) {
            errors.phone = 'Ingresa tu número celular a 10 dígitos numéricos.';
        }
        if (!formData.acceptedTerms) {
            errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones y Aviso de Privacidad.';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsRegistering(true);
        try {
            const payload = {
                roomId: activeRoomId,
                fullName: formData.fullName.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.trim(),
                acceptedTerms: formData.acceptedTerms,
                deviceInfo: `${navigator.platform || 'Unknown'} - ${navigator.userAgent ? navigator.userAgent.slice(0, 100) : ''}`,
                userAgent: navigator.userAgent
            };

            const res = await RegisterParticipantBL(payload);

            if (res.success && res.data) {
                const data = res.data;

                // Caso A: Ya participó previamente
                if (data.status === 'ALREADY_PARTICIPATED') {
                    const existingData = {
                        participantId: data.participantId,
                        fullName: formData.fullName,
                        email: formData.email,
                        phone: formData.phone,
                        ticket: data.participationTicket,
                        previousScore: data.previousScore,
                        previousDecibelsPeak: data.previousDecibelsPeak,
                        previousRankingPosition: data.previousRankingPosition,
                        status: 'ALREADY_PARTICIPATED'
                    };
                    setParticipantData(existingData);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
                    setPhase(PHASES.ALREADY_PARTICIPATED);
                    return;
                }

                // Caso B: Registro exitoso
                trackEvent('form_complete');
                const sessionInfo = {
                    participantId: data.participantId,
                    sessionToken: data.sessionToken,
                    sessionTokenExpiresAt: data.sessionTokenExpiresAt,
                    ticket: data.participationTicket,
                    fullName: formData.fullName,
                    email: formData.email,
                    phone: formData.phone,
                    durationSeconds: data.durationSeconds || config.durationSeconds || 5,
                    enableAmbientNoiseCalibration: data.enableAmbientNoiseCalibration,
                    status: 'REGISTERED'
                };

                setParticipantData(sessionInfo);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionInfo));
                localStorage.setItem('personal_noisemeter_participant_id', data.participantId);

                // Avanzar a la pantalla de permiso de micrófono
                setPhase(PHASES.MIC_PERMISSION);
            } else {
                showToast(res.message || 'Error al completar el registro.', 'danger');
            }
        } catch (err) {
            showToast('Ocurrió un error de red al procesar tu registro.', 'danger');
        } finally {
            setIsRegistering(false);
        }
    };

    // =========================================================================
    // 8. PREPARACIÓN Y CONTEO REGRESIVO (3-2-1) CON VIBRACIÓN
    // =========================================================================
    const handleStartMicAndCountdown = async () => {
        trackEvent('game_start');
        try {
            // 1. Solicitar flujo de micrófono con procesamiento desactivado
            const stream = await requestMicrophoneStream();
            mediaStreamRef.current = stream;

            // 2. Instanciar motor acústico Web Audio API
            const engine = createAudioEngine(stream);
            audioEngineRef.current = engine;

            // 3. Activar Screen WakeLock
            await acquireWakeLock();

            // 4. Cambiar a fase de conteo
            setCountdown(3);
            ambientSamplesRef.current = [];
            setPhase(PHASES.COUNTDOWN);

            triggerVibration([100]);
        } catch (err) {
            console.error('Error al solicitar permiso de micrófono:', err);
            showToast('No se pudo acceder al micrófono. Por favor habilita los permisos de audio en tu navegador.', 'danger', 6000);
        }
    };

    // Control del temporizador de conteo regresivo
    useEffect(() => {
        let timer = null;

        if (phase === PHASES.COUNTDOWN) {
            // Muestrear ruido base del micrófono durante el conteo para calibrar la compuerta acústica (Noise Gate)
            const sampleInterval = setInterval(() => {
                if (audioEngineRef.current) {
                    const rawDb = audioEngineRef.current.getRawDecibels
                        ? audioEngineRef.current.getRawDecibels()
                        : audioEngineRef.current.getDecibels();
                    if (rawDb > 0) {
                        ambientSamplesRef.current.push(rawDb);
                    }
                }
            }, 80);

            timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev > 1) {
                        triggerVibration([100]);
                        return prev - 1;
                    }

                    // Termina conteo, inicia RECORDING
                    clearInterval(sampleInterval);
                    clearInterval(timer);

                    // Calcular promedio de ruido base ambiental del micrófono
                    if (ambientSamplesRef.current.length > 0) {
                        const sum = ambientSamplesRef.current.reduce((a, b) => a + b, 0);
                        ambientNoiseBaselineRef.current = Math.round((sum / ambientSamplesRef.current.length) * 10) / 10;
                    } else {
                        ambientNoiseBaselineRef.current = 45.0; // Piso acústico estándar de seguridad
                    }

                    triggerVibration([300, 100, 300]);
                    startScreamRecording();
                    return 0;
                });
            }, 1000);

            return () => {
                if (timer) clearInterval(timer);
                if (sampleInterval) clearInterval(sampleInterval);
            };
        }
    }, [phase]);

    // =========================================================================
    // 9. BUCLE DE GRABACIÓN ACÚSTICA A 60 FPS (RECORDING)
    // =========================================================================
    const startScreamRecording = useCallback(() => {
        setPhase(PHASES.RECORDING);
        isRecordingActiveRef.current = true;
        recordingSamplesRef.current = [];
        livePeakRef.current = 0;
        setPeakDecibels(0);
        setCurrentDecibels(0);

        const duration = participantData?.durationSeconds || config.durationSeconds || 5;
        setRemainingSeconds(duration);
        recordingStartTimeRef.current = performance.now();

        const recordingDurationMs = duration * 1000;

        const loop = () => {
            if (!isRecordingActiveRef.current) return;

            const now = performance.now();
            const elapsed = now - recordingStartTimeRef.current;
            const remaining = Math.max(0, (recordingDurationMs - elapsed) / 1000);
            setRemainingSeconds(Math.round(remaining * 10) / 10);

            if (audioEngineRef.current) {
                const db = audioEngineRef.current.getDecibels(ambientNoiseBaselineRef.current || 45.0);
                setCurrentDecibels(db);
                recordingSamplesRef.current.push(db);

                if (db > livePeakRef.current) {
                    livePeakRef.current = db;
                    setPeakDecibels(db);
                }
            }

            if (elapsed >= recordingDurationMs) {
                // Finalizar grabación
                stopScreamRecordingAndSubmit();
                return;
            }

            animationFrameRef.current = requestAnimationFrame(loop);
        };

        animationFrameRef.current = requestAnimationFrame(loop);
    }, [participantData, config]);

    // Detener grabación y procesar puntuación
    const stopScreamRecordingAndSubmit = useCallback(async () => {
        isRecordingActiveRef.current = false;
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Liberar hardware de micrófono
        try {
            if (audioEngineRef.current) {
                await audioEngineRef.current.cleanup();
                audioEngineRef.current = null;
            }
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(t => t.stop());
                mediaStreamRef.current = null;
            }
        } catch (e) {
            console.warn('Error en cleanup de audio:', e);
        }

        // Cálculo de métricas
        const samples = recordingSamplesRef.current;
        const activeSamples = samples.filter(s => s > 0);
        const peak = livePeakRef.current || (activeSamples.length > 0 ? Math.max(...activeSamples) : 0);
        const avg = activeSamples.length > 0 ? activeSamples.reduce((a, b) => a + b, 0) / activeSamples.length : (peak > 0 ? peak * 0.75 : 0);
        const decibelsAverage = Math.round(avg * 10) / 10;
        const decibelsPeak = Math.round(peak * 10) / 10;

        triggerVibration([150, 100, 150]);
        setPhase(PHASES.SUBMITTING);

        // Envío con cola de reintentos
        dispatchSubmitScoreWithRetry({
            participantId: participantData.participantId,
            sessionToken: participantData.sessionToken,
            decibelsPeak,
            decibelsAverage,
            ambientNoiseBaseline: ambientNoiseBaselineRef.current || null,
            rawDurationMs: (participantData.durationSeconds || 5) * 1000
        }, 1);
    }, [participantData]);

    // =========================================================================
    // 10. ENVÍO DE SCORE CON REINTENTOS AUTOMÁTICOS ANTE CAÍDAS DE RED
    // =========================================================================
    const dispatchSubmitScoreWithRetry = async (payload, attemptNumber = 1) => {
        setSubmitRetryCount(attemptNumber);
        setSubmitError(null);

        try {
            const res = await SubmitScoreBL(payload);

            if (res.success && res.data) {
                const data = res.data;
                const finalResultObj = {
                    score: data.finalScore,
                    decibelsPeak: data.decibelsPeak,
                    decibelsAverage: data.decibelsAverage,
                    rankingPosition: data.rankingPosition,
                    totalParticipants: data.totalParticipants,
                    category: data.category || determineScreamCategory(data.decibelsPeak),
                    ticket: data.participationTicket,
                    playedAt: data.playedAt
                };

                trackEvent('game_complete');
                setOfficialResult(finalResultObj);

                // Persistir resultado final en LocalStorage
                const updatedSession = {
                    ...participantData,
                    status: 'COMPLETED',
                    result: finalResultObj
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));

                // Liberar WakeLock
                await releaseWakeLockSafely();

                triggerVibration([200, 100, 200, 100, 400]);
                setPhase(PHASES.RESULT);
            } else {
                throw new Error(res.message || 'Error en la respuesta del servidor.');
            }
        } catch (err) {
            console.warn(`Error al enviar puntaje (intento ${attemptNumber}/5):`, err);

            if (attemptNumber < 5) {
                setSubmitError(`Problema de conexión. Reintentando en 3s (Intento ${attemptNumber + 1} de 5)...`);
                setTimeout(() => {
                    dispatchSubmitScoreWithRetry(payload, attemptNumber + 1);
                }, 3000);
            } else {
                setSubmitError('No se pudo conectar con el servidor tras 5 intentos. Por favor verifica tu conexión y presiona reintentar.');
            }
        }
    };

    // =========================================================================
    // 10.1 REGISTRAR A OTRO AFICIONADO EN EL MISMO DISPOSITIVO (MODO COMPARTIDO / KIOSCO)
    // =========================================================================
    const handleNewParticipantOnSameDevice = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
            // Generar un nuevo identificador de sesión para que el nuevo participante tenga su propio embudo de telemetría independiente
            const newSessionId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('pm_session_id', newSessionId);
            sessionStorage.removeItem('pm_form_start_tracked');
        } catch (e) {
            // Ignorar
        }
        setOfficialResult(null);
        setParticipantData(null);
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            acceptedTerms: false
        });
        setFormErrors({});
        setFormStartTracked(false);
        setPhase(PHASES.REGISTER);
        showToast('Formulario listo para registrar a otro aficionado.', 'info', 3500);
    }, [showToast]);

    // =========================================================================
    // 11. CONSULTA DE RANKING EN VIVO (MODAL)
    // =========================================================================
    const handleOpenRankingModal = async () => {
        setIsRankingModalOpen(true);
        setIsLoadingRanking(true);
        try {
            const res = await GetRankingBL(activeRoomId, 50);
            if (res.success && res.data) {
                setRankingList(res.data.topRanking || []);
                setRankingStats({
                    totalParticipants: res.data.totalParticipants,
                    recordPeakDecibels: res.data.recordPeakDecibels,
                    averageScore: res.data.averageScore
                });
            }
        } catch (e) {
            showToast('Error al consultar la tabla de clasificación.', 'danger');
        } finally {
            setIsLoadingRanking(false);
        }
    };

    const filteredRanking = useMemo(() => {
        if (!rankingSearch.trim()) return rankingList;
        const q = rankingSearch.trim().toLowerCase();
        return rankingList.filter(item =>
            (item.displayName && item.displayName.toLowerCase().includes(q)) ||
            (item.ticket && item.ticket.toLowerCase().includes(q))
        );
    }, [rankingList, rankingSearch]);

    // =========================================================================
    // 12. CÁLCULO DE CATEGORÍA Y MEDALLA FESTIVA
    // =========================================================================
    const getCategoryBadge = (category, peak) => {
        const p = peak || 0;
        if (p >= 110.0 || category?.includes('Legendario')) {
            return {
                title: '¡Grito Legendario!',
                badgeColor: '#ffc107',
                icon: 'trophy-fill',
                desc: '¡Retumbó con la fuerza de los héroes de la patria!',
                glowClass: 'legendary-glow'
            };
        }
        if (p >= 95.0 || category?.includes('Patriota')) {
            return {
                title: '¡Grito Patriota!',
                badgeColor: '#ce1126',
                icon: 'fire',
                desc: '¡Un grito con el alma y orgullo mexicano!',
                glowClass: 'patriota-glow'
            };
        }
        if (p >= 80.0 || category?.includes('Bravo')) {
            return {
                title: '¡Grito Bravo!',
                badgeColor: '#006847',
                icon: 'megaphone-fill',
                desc: '¡Gran entusiasmo y energía festiva!',
                glowClass: 'bravo-glow'
            };
        }
        return {
            title: '¡Grito con Orgullo!',
            badgeColor: '#0dcaf0',
            icon: 'heart-fill',
            desc: '¡Gracias por celebrar con nosotros!',
            glowClass: 'orgullo-glow'
        };
    };

    // Helper modal de Términos y Condiciones / Aviso de Privacidad
    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 110000, backdropFilter: 'blur(4px)' }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    // =========================================================================
    // 13. RENDERIZADO POR FASE
    // =========================================================================

    // FASE: LOADING
    if (phase === PHASES.LOADING) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-dark text-white p-4">
                <div className="spinner-border text-success mb-3" style={{ width: '3rem', height: '3rem' }} role="status" />
                <h4 className="fw-bold">Cargando Grito Mexicano...</h4>
                <p className="text-secondary small">Conectando con la sala oficial</p>
            </div>
        );
    }

    // FASE: CLOSED
    if (phase === PHASES.CLOSED) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-dark text-white p-4 text-center">
                <div className="p-4 rounded-circle bg-danger bg-opacity-10 mb-3 border border-danger">
                    <i className="bi bi-clock-history text-danger fs-1" />
                </div>
                <h3 className="fw-bold mb-2">Dinámica No Disponible</h3>
                <p className="text-secondary" style={{ maxWidth: '400px' }}>
                    La dinámica de Grito Mexicano se encuentra actualmente pausada o finalizada por el operador de la sala.
                </p>
                <button className="btn btn-outline-light mt-3 px-4 rounded-pill" onClick={loadPublicConfig}>
                    <i className="bi bi-arrow-clockwise me-2" /> Actualizar Estado
                </button>
            </div>
        );
    }

    return (
        <div
            className="personal-noisemeter-fan-root min-vh-100 d-flex flex-column justify-content-between position-relative"
            style={{
                backgroundColor: config.backgroundColor || '#121212',
                color: '#f8f9fa',
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}
        >
            {/* Toast Notifications */}
            {toastMessage && (
                <div
                    className={`position-fixed top-0 start-50 translate-middle-x mt-3 z-3 alert alert-${toastMessage.type} shadow-lg py-2 px-4 rounded-pill text-center d-flex align-items-center gap-2`}
                    style={{ zIndex: 99999, maxWidth: '90vw' }}
                >
                    <i className="bi bi-info-circle-fill" />
                    <span className="small fw-semibold">{toastMessage.msg}</span>
                </div>
            )}

            {/* Modales Legales */}
            {showTermsModal && renderLegalModal('Términos y Condiciones', DEFAULT_TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', DEFAULT_PRIVACY_TEXT, setShowPrivacyModal)}

            {/* Header Patriota Superior */}
            {/* <header className="px-3 py-3 border-bottom border-dark d-flex align-items-center justify-content-between bg-black bg-opacity-40">
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                        style={{ width: '36px', height: '36px', backgroundColor: '#006847', border: '2px solid #ffc107' }}
                    >
                        <i className="bi bi-mic-fill text-white fs-6" />
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold text-white tracking-wide" style={{ letterSpacing: '0.5px' }}>
                            {config.headerTitle || '¡Grito Mexicano!'}
                        </h6>
                        <span className="text-warning small" style={{ fontSize: '11px' }}>
                            <i className="bi bi-broadcast me-1" /> Edición Bicentenario
                        </span>
                    </div>
                </div>

                <button
                    className="btn btn-sm btn-outline-warning rounded-pill px-3 py-1 d-flex align-items-center gap-1"
                    onClick={handleOpenRankingModal}
                    title="Ver Ranking en Vivo"
                >
                    <i className="bi bi-trophy-fill" />
                    <span className="d-none d-sm-inline small">Ranking</span>
                </button>
            </header> */}

            {/* CUERPO PRINCIPAL CONDICIONAL */}
            <main className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-3 text-center position-relative">

                {/* ============================================================= */}
                {/* 1. FASE: REGISTER (Formulario de Registro) */}
                {/* ============================================================= */}
                {phase === PHASES.REGISTER && (
                    <div className="w-100 py-3" style={{ maxWidth: '440px' }}>
                        <div className="mb-4 text-center">
                            <div className="d-inline-flex p-3 rounded-circle bg-danger bg-opacity-20 border border-success border-opacity-50 mb-3">
                                <i className="bi bi-megaphone text-white fs-1" />
                            </div>
                            <h3 className="fw-bold text-white mb-1">{config.headerTitle || '¡Grito Mexicano!'}</h3>
                            <p className="text-secondary small mb-0">{config.headerSubtitle || 'Registra tus datos para participar y medir la fuerza de tu voz.'}</p>
                        </div>

                        <div className="card bg-dark border-secondary border-opacity-25 shadow-lg rounded-4 text-start">
                            <div className="card-body p-4">
                                <form onSubmit={handleRegisterSubmit} noValidate>
                                    {/* Nombre Completo */}
                                    <div className="mb-3">
                                        <label className="form-label text-light small fw-bold">
                                            Nombre Completo <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-black border-secondary text-secondary">
                                                <i className="bi bi-person-fill" />
                                            </span>
                                            <input
                                                type="text"
                                                name="fullName"
                                                className={`form-control bg-black text-white border-secondary ${formErrors.fullName ? 'is-invalid' : ''}`}
                                                //placeholder="Ej. Juan Pérez García"
                                                value={formData.fullName}
                                                onChange={handleInputChange}
                                                onFocus={handleFormStart}
                                                required
                                            />
                                        </div>
                                        {formErrors.fullName && (
                                            <div className="text-danger small mt-1">{formErrors.fullName}</div>
                                        )}
                                    </div>

                                    {/* Correo Electrónico */}
                                    <div className="mb-3">
                                        <label className="form-label text-light small fw-bold">
                                            Correo Electrónico <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-black border-secondary text-secondary">
                                                <i className="bi bi-envelope-fill" />
                                            </span>
                                            <input
                                                type="email"
                                                name="email"
                                                className={`form-control bg-black text-white border-secondary ${formErrors.email ? 'is-invalid' : ''}`}
                                                //placeholder="juan.perez@ejemplo.com"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                onFocus={handleFormStart}
                                                required
                                            />
                                        </div>
                                        {formErrors.email && (
                                            <div className="text-danger small mt-1">{formErrors.email}</div>
                                        )}
                                    </div>

                                    {/* Teléfono a 10 Dígitos */}
                                    <div className="mb-3">
                                        <label className="form-label text-light small fw-bold">
                                            Teléfono Celular (10 dígitos) <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-black border-secondary text-secondary">
                                                <i className="bi bi-telephone-fill" />
                                            </span>
                                            <input
                                                type="tel"
                                                name="phone"
                                                maxLength="10"
                                                className={`form-control bg-black text-white border-secondary ${formErrors.phone ? 'is-invalid' : ''}`}
                                                //placeholder="5512345678"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                onFocus={handleFormStart}
                                                required
                                            />
                                        </div>
                                        {formErrors.phone && (
                                            <div className="text-danger small mt-1">{formErrors.phone}</div>
                                        )}
                                    </div>

                                    {/* Términos y Condiciones */}
                                    <div className="mb-4 form-check text-start">
                                        <input
                                            type="checkbox"
                                            name="acceptedTerms"
                                            id="termsCheck"
                                            className={`form-check-input bg-black border-secondary ${formErrors.acceptedTerms ? 'is-invalid' : ''}`}
                                            checked={formData.acceptedTerms}
                                            onChange={handleInputChange}
                                            onFocus={handleFormStart}
                                            required
                                        />
                                        <label className="form-check-label text-secondary small" htmlFor="termsCheck" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                                            He leído y acepto los <button type="button" className="btn btn-link p-0 text-warning small border-0 align-baseline text-decoration-underline" style={{ fontSize: '12px' }} onClick={() => setShowTermsModal(true)}>Términos y Condiciones</button> y el <button type="button" className="btn btn-link p-0 text-warning small border-0 align-baseline text-decoration-underline" style={{ fontSize: '12px' }} onClick={() => setShowPrivacyModal(true)}>Aviso de Privacidad</button> de la dinámica.
                                        </label>
                                        {formErrors.acceptedTerms && (
                                            <div className="text-danger small mt-1">{formErrors.acceptedTerms}</div>
                                        )}
                                    </div>

                                    {/* Botón de Registro */}
                                    <button
                                        type="submit"
                                        disabled={isRegistering}
                                        className="btn btn-danger w-100 py-3 rounded-pill fw-bold text-uppercase shadow-lg d-flex align-items-center justify-content-center gap-2"
                                        //style={{ backgroundColor: '#006847', borderColor: '#006847' }}
                                    >
                                        {isRegistering ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" />
                                                Registrando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check2-circle fs-5" />
                                                Continuar al Grito
                                            </>
                                        )}
                                    </button>
                                </form>

                                {/* Enlace a Consulta de Ranking en Vivo */}
                                <div className="text-center mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-link text-warning text-decoration-none small p-0 fw-semibold d-inline-flex align-items-center gap-1 opacity-90"
                                        style={{ fontSize: '13px' }}
                                        onClick={handleOpenRankingModal}
                                    >
                                        <i className="bi bi-trophy-fill" />
                                        <span>Consultar ranking en vivo</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 2. FASE: ALREADY_PARTICIPATED */}
                {/* ============================================================= */}
                {phase === PHASES.ALREADY_PARTICIPATED && (
                    <div className="w-100 py-4 text-center" style={{ maxWidth: '440px' }}>
                        <div className="p-4 rounded-circle bg-warning bg-opacity-10 border border-warning d-inline-flex mb-3">
                            <i className="bi bi-patch-check-fill text-warning fs-1" />
                        </div>
                        <h3 className="fw-bold text-white mb-2">¡Gracias por tu participación!</h3>
                        <p className="text-secondary small mb-4">
                            Registramos previamente tu participación en esta dinámica con el ticket:
                        </p>

                        <div className="card bg-dark border-secondary border-opacity-50 rounded-4 p-4 mb-4 shadow">
                            <span className="badge bg-secondary mb-2 align-self-center px-3 py-1">Tu ticket</span>
                            <h2 className="fw-black text-warning tracking-wider mb-3">
                                #{participantData?.ticket || 'GM-0000'}
                            </h2>

                            <div className="row g-2 text-center pt-3 border-top border-secondary border-opacity-25">
                                <div className="col-6">
                                    <div className="p-2 bg-black bg-opacity-50 rounded-3">
                                        <div className="text-secondary small" style={{ fontSize: '11px' }}>PICO</div>
                                        <div className="fs-5 fw-bold text-white">{participantData?.previousDecibelsPeak || 0}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-2 bg-black bg-opacity-50 rounded-3">
                                        <div className="text-secondary small" style={{ fontSize: '11px' }}>POSICIÓN MOMENTÁNEA</div>
                                        <div className="fs-5 fw-bold text-success">#{participantData?.previousRankingPosition || '-'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-grid gap-2">
                            <button
                                className="btn btn-warning py-3 rounded-pill fw-bold text-dark shadow"
                                onClick={handleOpenRankingModal}
                            >
                                <i className="bi bi-trophy-fill me-2" />
                                Ver ranking
                            </button>

                            {config.allowMultipleParticipantsOnSameDevice && (
                                <button
                                    className="btn btn-outline-light py-3 rounded-pill fw-bold shadow mt-1"
                                    onClick={handleNewParticipantOnSameDevice}
                                >
                                    <i className="bi bi-person-plus-fill me-2" />
                                    Registrar a otr@ participante
                                </button>
                            )}
                        </div>

                        {/* <p className="text-secondary small mt-4" style={{ fontSize: '11px' }}>
                            <i className="bi bi-info-circle me-1" />
                            Si ocurrió algún inconveniente técnico, solicítale al operador en cabina que reactive tu turno.
                        </p> */}

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 3. FASE: MIC_PERMISSION (Instrucciones Previas al Grito) */}
                {/* ============================================================= */}
                {phase === PHASES.MIC_PERMISSION && (
                    <div className="w-100 py-3 text-center" style={{ maxWidth: '440px' }}>
                        <div className="position-relative d-inline-block mb-3">
                            <div className="p-4 rounded-circle bg-danger bg-opacity-20 border border-success border-opacity-50">
                                <i className="bi bi-mic-fill text-white" style={{ fontSize: '3rem' }} />
                            </div>
                            <span className="position-absolute bottom-0 end-0 badge rounded-pill bg-danger border border-white p-2">
                                <i className="bi bi-fire" />
                            </span>
                        </div>

                        <h3 className="fw-bold text-white mb-2">¡Todo listo para gritar!</h3>
                        <p className="text-secondary small mb-4">
                            Sigue estas recomendaciones para obtener la máxima potencia en tu medición:
                        </p>

                        <div className="card bg-dark border-secondary border-opacity-25 rounded-4 p-3 text-start mb-4 shadow-sm">
                            <div className="d-flex align-items-start gap-3 mb-3">
                                <div className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center fw-bold" style={{ width: '28px', height: '28px', minWidth: '28px' }}>1</div>
                                <div>
                                    <div className="text-white fw-semibold small">Sujeta tu teléfono</div>
                                    <div className="text-secondary" style={{ fontSize: '12px' }}>Coloca el micrófono inferior a unos 15 cm de tu boca.</div>
                                </div>
                            </div>
                            <div className="d-flex align-items-start gap-3 mb-3">
                                <div className="rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center fw-bold" style={{ width: '28px', height: '28px', minWidth: '28px' }}>2</div>
                                <div>
                                    <div className="text-white fw-semibold small">Conteo de 3 segundos</div>
                                    <div className="text-secondary" style={{ fontSize: '12px' }}>Toma aire profundamente durante la cuenta regresiva.</div>
                                </div>
                            </div>
                            <div className="d-flex align-items-start gap-3">
                                <div className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center fw-bold" style={{ width: '28px', height: '28px', minWidth: '28px' }}>3</div>
                                <div>
                                    <div className="text-white fw-semibold small">¡Grita con el corazón!</div>
                                    <div className="text-secondary" style={{ fontSize: '12px' }}>Sostén tu grito patrio durante los {participantData?.durationSeconds || config.durationSeconds || 5} segundos.</div>
                                </div>
                            </div>
                        </div>

                        <button
                            className="btn btn-danger w-100 py-3 rounded-pill fw-bold text-uppercase shadow-lg fs-5 d-flex align-items-center justify-content-center gap-2 pulse-btn"
                            //style={{ backgroundColor: '#006847', borderColor: '#ffc107', borderWidth: '2px' }}
                            style={{ borderWidth: '2px' }}
                            onClick={handleStartMicAndCountdown}
                        >
                            <i className="bi bi-play-circle-fill fs-4" />
                            ¡Comenzar mi grito!
                        </button>

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 4. FASE: COUNTDOWN (3-2-1 Conteo con Vibración) */}
                {/* ============================================================= */}
                {phase === PHASES.COUNTDOWN && (
                    <div className="d-flex flex-column align-items-center justify-content-center w-100 py-5">
                        <span className="text-warning text-uppercase fw-bold tracking-wider mb-2">
                            <i className="bi bi-lightning-charge-fill me-1" /> Prepárate
                        </span>
                        <h4 className="text-secondary fw-semibold mb-4">Toma aire profundamente...</h4>

                        <div className="position-relative d-flex align-items-center justify-content-center my-4">
                            <div className="countdown-pulse-ring" />
                            <div
                                className="rounded-circle bg-dark border border-warning d-flex align-items-center justify-content-center shadow-lg"
                                style={{ width: '180px', height: '180px', borderWidth: '4px' }}
                            >
                                <span className="display-1 fw-black text-warning countdown-number">
                                    {countdown}
                                </span>
                            </div>
                        </div>

                        {/* <div className="text-secondary small mt-4">
                            Calibrando acústica ambiental del micrófono...
                        </div> */}

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 5. FASE: RECORDING (Grabación a 60 FPS con Sonómetro Radial) */}
                {/* ============================================================= */}
                {phase === PHASES.RECORDING && (
                    <div className="w-100 d-flex flex-column align-items-center justify-content-center py-2" style={{ maxWidth: '420px' }}>
                        {/* Indicador superior de GRITA AHORA */}
                        <div className="badge bg-danger text-white px-4 py-2 rounded-pill fs-6 mb-3 shadow-lg d-flex align-items-center gap-2 animate-bounce">
                            <i className="bi bi-fire fs-5" />
                            <span className="fw-black tracking-wider">¡GRITAAAA CON ORGULLO!</span>
                        </div>

                        {/* Sonómetro Circular Radial SVG */}
                        <div className="position-relative d-flex align-items-center justify-content-center my-2">
                            <svg className="radial-noisemeter-svg" width="260" height="260" viewBox="0 0 260 260">
                                {/* Fondo del anillo */}
                                <circle
                                    cx="130"
                                    cy="130"
                                    r="110"
                                    fill="none"
                                    stroke="rgba(255, 255, 255, 0.1)"
                                    strokeWidth="16"
                                />
                                {/* Anillo de Progreso de Decibeles (0 - 120 dB) */}
                                <circle
                                    cx="130"
                                    cy="130"
                                    r="110"
                                    fill="none"
                                    stroke={currentDecibels >= 105 ? '#ce1126' : currentDecibels >= 85 ? '#ffc107' : '#006847'}
                                    strokeWidth="16"
                                    strokeDasharray={2 * Math.PI * 110}
                                    strokeDashoffset={2 * Math.PI * 110 * (1 - Math.min(1, currentDecibels / 120))}
                                    strokeLinecap="round"
                                    style={{
                                        transition: 'stroke-dashoffset 0.05s linear, stroke 0.2s ease',
                                        transform: 'rotate(-90deg)',
                                        transformOrigin: '50% 50%'
                                    }}
                                />
                            </svg>

                            {/* Contenido Central del Sonómetro */}
                            <div className="position-absolute d-flex flex-column align-items-center justify-content-center text-center">
                                <span className="display-3 fw-black text-white mb-0" style={{ lineHeight: 1 }}>
                                    {currentDecibels.toFixed(1)}
                                </span>
                                <span className="text-warning fw-bold fs-6">DECIBELES</span>
                                <span className="badge bg-black bg-opacity-75 text-secondary border border-secondary border-opacity-50 mt-1 px-3 py-1 rounded-pill small">
                                    Pico: <strong className="text-success">{peakDecibels.toFixed(1)}</strong>
                                </span>
                            </div>
                        </div>

                        {/* Barra de Tiempo Regresivo */}
                        <div className="w-100 px-3 mt-3">
                            <div className="d-flex justify-content-between text-secondary small mb-1 fw-bold">
                                <span>TIEMPO RESTANTE</span>
                                <span className="text-warning">{remainingSeconds.toFixed(1)}s</span>
                            </div>
                            <div className="progress bg-black border border-secondary border-opacity-50" style={{ height: '12px', borderRadius: '6px' }}>
                                <div
                                    className="progress-bar bg-warning progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{
                                        width: `${(remainingSeconds / (participantData?.durationSeconds || config.durationSeconds || 5)) * 100}%`,
                                        transition: 'width 0.1s linear'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Ondas / Flamas de Audio Animadas */}
                        <div className="d-flex align-items-end justify-content-center gap-1 mt-4" style={{ height: '40px', width: '100%' }}>
                            {[0.4, 0.7, 1.0, 0.8, 0.5, 0.9, 1.2, 0.6, 0.9, 0.5].map((scale, i) => {
                                const barHeight = currentDecibels > 0 ? Math.max(8, Math.min(38, (currentDecibels / 120) * 38 * scale)) : 4;
                                return (
                                    <div
                                        key={i}
                                        className="rounded-pill"
                                        style={{
                                            width: '6px',
                                            height: `${barHeight}px`,
                                            backgroundColor: i % 3 === 0 ? '#006847' : i % 3 === 1 ? '#f8f9fa' : '#ce1126',
                                            transition: 'height 0.05s ease'
                                        }}
                                    />
                                );
                            })}
                        </div>

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 6. FASE: SUBMITTING (Transmitiendo y Reintentando) */}
                {/* ============================================================= */}
                {phase === PHASES.SUBMITTING && (
                    <div className="d-flex flex-column align-items-center justify-content-center w-100 py-5 text-center" style={{ maxWidth: '400px' }}>
                        <div className="spinner-border text-warning mb-4" style={{ width: '4rem', height: '4rem' }} role="status" />
                        <h4 className="fw-bold text-white mb-2">Registrando Tu Grito...</h4>
                        <p className="text-secondary small mb-3">
                            {submitRetryCount > 1 ? `Reintento ${submitRetryCount} de 5...` : 'Validando telemetría oficial y calculando posición en ranking...'}
                        </p>

                        {submitError && (
                            <div className="alert alert-danger w-100 py-2 px-3 small rounded-3 mt-2">
                                <i className="bi bi-exclamation-triangle-fill me-1" />
                                {submitError}
                            </div>
                        )}

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 7. FASE: RESULT (Tarjeta de Celebración, Confeti y Medalla) */}
                {/* ============================================================= */}
                {phase === PHASES.RESULT && officialResult && (
                    <div className="w-100 py-2 text-center position-relative" style={{ maxWidth: '440px' }}>
                        {/* Confeti Digital Canvas / Partículas */}
                        <div className="confetti-container" aria-hidden="true">
                            {Array.from({ length: 30 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="confetti-particle"
                                    style={{
                                        left: `${(i * 3.33)}%`,
                                        animationDelay: `${(i % 5) * 0.4}s`,
                                        backgroundColor: i % 3 === 0 ? '#006847' : i % 3 === 1 ? '#ffc107' : '#ce1126'
                                    }}
                                />
                            ))}
                        </div>

                        {/* Medalla e Insignia */}
                        {(() => {
                            const badge = getCategoryBadge(officialResult.category, officialResult.decibelsPeak);
                            return (
                                <div className="mb-2">
                                    <div
                                        className={`d-inline-flex p-3 rounded-circle shadow-lg mb-2 border ${badge.glowClass}`}
                                        style={{ backgroundColor: badge.badgeColor, borderColor: '#ffffff' }}
                                    >
                                        <i className={`bi bi-${badge.icon} text-dark fs-1`} />
                                    </div>
                                    <h3 className="fw-black text-white mb-0">{badge.title}</h3>
                                    <p className="text-warning small mb-3">{badge.desc}</p>
                                </div>
                            );
                        })()}

                        {/* Tarjeta de Métricas Oficiales */}
                        <div className="card bg-dark border-secondary border-opacity-50 rounded-4 shadow-lg p-3 mb-3 text-start position-relative overflow-hidden">
                            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-secondary border-opacity-25">
                                <div>
                                    <span className="text-secondary small d-block" style={{ fontSize: '11px' }}>TICKET DE PARTICIPACIÓN</span>
                                    <span className="fw-bold text-warning fs-5">#{officialResult.ticket || participantData?.ticket || 'GM-0000'}</span>
                                </div>
                                <div className="text-end">
                                    <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50 px-3 py-1 rounded-pill">
                                        Oficial
                                    </span>
                                </div>
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-6">
                                    <div className="p-3 bg-black bg-opacity-60 rounded-3 text-center border border-secondary border-opacity-25">
                                        <div className="text-secondary small fw-bold" style={{ fontSize: '11px' }}>PICO</div>
                                        <div className="display-6 fw-black text-white mt-1">
                                            {officialResult.decibelsPeak?.toFixed(1)} <span className="fs-6 fw-normal text-secondary">dB</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-3 bg-black bg-opacity-60 rounded-3 text-center border border-secondary border-opacity-25">
                                        <div className="text-secondary small fw-bold" style={{ fontSize: '11px' }}>POSICIÓN MOMENTÁNEA</div>
                                        <div className="display-6 fw-black text-warning mt-1">
                                            #{officialResult.rankingPosition || '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {config.enableScoreFormula && (
                                <div className="p-2 bg-black bg-opacity-40 rounded-3 d-flex justify-content-between align-items-center small px-3">
                                    <span className="text-secondary">Puntuación ponderada:</span>
                                    <span className="fw-bold text-light fs-6">{officialResult.score?.toFixed(2)} pts</span>
                                </div>
                            )}
                        </div>

                        {/* Acciones */}
                        <div className="d-grid gap-2">
                            <button
                                className="btn btn-outline-light py-3 rounded-pill fw-bold text-dark shadow-lg fs-6"
                                onClick={handleOpenRankingModal}
                            >
                                <i className="bi bi-trophy-fill me-2" />
                                Consultar Tabla de Clasificación
                            </button>

                            {config.allowMultipleParticipantsOnSameDevice && (
                                <button
                                    className="btn btn-outline-danger py-3 rounded-pill fw-bold shadow-lg fs-6 mt-1"
                                    onClick={handleNewParticipantOnSameDevice}
                                >
                                    <i className="bi bi-person-plus-fill me-2" />
                                    Registrar a otro participante
                                </button>
                            )}
                        </div>

                        <p className='d-flex align-items-center justify-content-center mt-4 small'>Powered by <img src='/img/page/xolos-five.png' style={{ width: '200px' }} /></p>
                    </div>
                )}
            </main>

            {/* Footer */}
            {/* <footer className="py-2 text-center text-secondary small border-top border-dark bg-black bg-opacity-40" style={{ fontSize: '11px' }}>
                Interaktia Grito Mexicano &bull; Powered by Web Audio API 60 FPS
            </footer> */}

            {/* ============================================================= */}
            {/* MODAL: TABLA DE CLASIFICACIÓN EN VIVO (RANKING) */}
            {/* ============================================================= */}
            {isRankingModalOpen && (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)', zIndex: 100000 }}
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content bg-dark text-white border-secondary rounded-4 shadow-2xl">
                            <div className="modal-header border-secondary border-opacity-25 py-3">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-trophy-fill text-warning fs-5" />
                                    <h5 className="modal-title fw-bold">Ranking en Vivo</h5>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setIsRankingModalOpen(false)}
                                />
                            </div>

                            <div className="modal-body p-3">
                                {/* Estadísticas Rápidas */}
                                {rankingStats && (
                                    <div className="row g-2 mb-3 text-center">
                                        <div className="col-4">
                                            <div className="p-2 bg-black bg-opacity-60 rounded-3 border border-secondary border-opacity-25">
                                                <div className="text-secondary" style={{ fontSize: '10px' }}>PARTICIPANTES</div>
                                                <div className="fw-bold text-white small">{rankingStats.totalParticipants || 0}</div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2 bg-black bg-opacity-60 rounded-3 border border-secondary border-opacity-25">
                                                <div className="text-secondary" style={{ fontSize: '10px' }}>RÉCORD PICO</div>
                                                <div className="fw-bold text-warning small">{rankingStats.recordPeakDecibels || 0}</div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2 bg-black bg-opacity-60 rounded-3 border border-secondary border-opacity-25">
                                                <div className="text-secondary" style={{ fontSize: '10px' }}>PROMEDIO</div>
                                                <div className="fw-bold text-success small">
                                                    {rankingStats.averageScore || 0} {config.enableScoreFormula ? 'pts' : 'dB'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Barra de Búsqueda */}
                                <div className="input-group mb-3">
                                    <span className="input-group-text bg-black border-secondary text-secondary">
                                        <i className="bi bi-search" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control bg-black text-white border-secondary"
                                        placeholder="Buscar por nombre o ticket..."
                                        value={rankingSearch}
                                        onChange={(e) => setRankingSearch(e.target.value)}
                                    />
                                    {rankingSearch && (
                                        <button className="btn btn-outline-secondary" onClick={() => setRankingSearch('')}>
                                            <i className="bi bi-x" />
                                        </button>
                                    )}
                                </div>

                                {/* Listado del Ranking */}
                                {isLoadingRanking ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-warning spinner-border-sm mb-2" />
                                        <div className="text-secondary small">Cargando clasificación...</div>
                                    </div>
                                ) : filteredRanking.length === 0 ? (
                                    <div className="text-center py-4 text-secondary small">
                                        No se encontraron participantes en el ranking.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {filteredRanking.map((item, idx) => {
                                            const isUser = participantData?.ticket && item.ticket === participantData.ticket;
                                            return (
                                                <div
                                                    key={idx}
                                                    className={`p-2 rounded-3 d-flex align-items-center justify-content-between border ${isUser ? 'bg-success bg-opacity-20 border-success' : 'bg-black bg-opacity-40 border-secondary border-opacity-25'}`}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className={`rounded-circle d-flex align-items-center justify-content-center fw-bold small ${item.position === 1 ? 'bg-warning text-dark' : item.position === 2 ? 'bg-light text-dark' : item.position === 3 ? 'bg-danger text-white' : 'bg-secondary text-white'}`}
                                                            style={{ width: '28px', height: '28px', minWidth: '28px', fontSize: '11px' }}
                                                        >
                                                            {item.position}
                                                        </div>
                                                        <div>
                                                            <div className="text-white fw-semibold small d-flex align-items-center gap-1">
                                                                {item.displayName || 'Participante'}
                                                                {isUser && <span className="badge bg-success" style={{ fontSize: '9px' }}>TÚ</span>}
                                                            </div>
                                                            <div className="text-secondary" style={{ fontSize: '11px' }}>
                                                                Ticket: <strong className="text-warning">#{item.ticket}</strong>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="text-end">
                                                        <div className="fw-bold text-white small">{item.decibelsPeak} dB</div>
                                                        {config.enableScoreFormula ? (
                                                            <div className="text-secondary" style={{ fontSize: '10px' }}>{item.score} pts</div>
                                                        ) : (
                                                            item.category ? <div className="text-secondary" style={{ fontSize: '10px' }}>{item.category}</div> : null
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer border-secondary border-opacity-25 py-2">
                                <button
                                    className="btn btn-secondary w-100 rounded-pill py-2"
                                    onClick={() => setIsRankingModalOpen(false)}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const PersonalNoisemeterFan = PersonalNoisemeter;
export default PersonalNoisemeter;
